"use client"

import { useState, useMemo } from "react"
import { Plus } from "lucide-react"
import { Button } from "../ui/button.tsx"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.tsx"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.tsx"

export function ParentDashboard({ childData = {} }) {
  const [currentDate] = useState(new Date())

  // Format date for display - memoized to prevent unnecessary recalculations
  const formattedDate = useMemo(() => {
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(currentDate)
  }, [currentDate])
  
  // Extract child information with defaults for safety
  const { 
    grade = 'A', 
    percentage = 87,
    lastUpdate = 'Jan 27'
  } = childData;

  return (
    <div className="flex flex-col gap-4 sm:gap-6 p-4 sm:p-6">
      {/* Grade Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="md:col-span-1 lg:col-span-2">
          <div className="flex flex-col">
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-700">Your Child's Average Grading Today</h1>
            <div className="flex items-center mt-4 sm:mt-6">
              <div className="text-7xl sm:text-8xl md:text-9xl lg:text-[120px] font-bold text-gray-800 leading-none">
                {grade},{percentage}%
              </div>
            </div>
            <div className="text-xs sm:text-sm text-gray-500 mt-2">
              Latest update added <span className="text-purple-600 font-medium">{lastUpdate}</span>
            </div>
          </div>
        </div>

        <div className="md:col-span-1">
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 pt-4">
              <CardTitle className="text-base sm:text-lg font-medium">Recent Events</CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full bg-purple-600 text-white hover:bg-purple-700 text-xs h-7 px-3"
              >
                Show All
              </Button>
            </CardHeader>
            <CardContent className="pt-2 px-4 pb-4">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                    <AvatarImage src="/placeholder.svg?height=40&width=40" alt="Tolulope Ayodeji" />
                    <AvatarFallback className="bg-gray-100 text-xs">TA</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-medium text-sm sm:text-base">Tolulope Ayodeji</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Teacher Coordinator</p>
                  </div>
                </div>

                <div className="bg-gray-100 p-3 sm:p-4 rounded-lg">
                  <h3 className="font-medium text-sm sm:text-base">Book Fair</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-4 sm:line-clamp-none">
                    Join us on March 5 for our Book Fair! Explore captivating reads for the entire family, seize the
                    opportunity to enrich your child's reading collection and support our school.
                  </p>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mt-3">
                    <div className="text-xs sm:text-sm text-gray-500">January 27, 1:43 PM</div>
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2">
                        <Avatar className="h-5 w-5 sm:h-6 sm:w-6 border-2 border-white">
                          <AvatarFallback className="bg-purple-200 text-[8px] sm:text-[10px]">U1</AvatarFallback>
                        </Avatar>
                        <Avatar className="h-5 w-5 sm:h-6 sm:w-6 border-2 border-white">
                          <AvatarFallback className="bg-purple-300 text-[8px] sm:text-[10px]">U2</AvatarFallback>
                        </Avatar>
                        <Avatar className="h-5 w-5 sm:h-6 sm:w-6 border-2 border-white">
                          <AvatarFallback className="bg-purple-400 text-[8px] sm:text-[10px]">U3</AvatarFallback>
                        </Avatar>
                      </div>
                      <span className="text-xs sm:text-sm text-gray-500">29 Comments</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full flex items-center justify-center gap-1 text-xs h-8"
                  >
                    <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Add to calendar</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upcoming Exams and Schedule */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div>
          <Card>
            <CardHeader className="px-4 pt-4 pb-2">
              <CardTitle className="text-base sm:text-lg font-medium">Upcoming Exams</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-4 pb-4">
              <div className="grid grid-cols-3 gap-2">
                <ExamCard day="22" subject="" dayOfWeek="Monday" isActive={false} />
                <ExamCard day="23" subject="Math" dayOfWeek="Tuesday" isActive={true} />
                <ExamCard day="24" subject="Art" dayOfWeek="Wednesday" isActive={true} />
                <ExamCard day="25" subject="" dayOfWeek="Thursday" isActive={false} />
                <ExamCard day="26" subject="" dayOfWeek="Friday" isActive={false} />
                <ExamCard day="27" subject="" dayOfWeek="Monday" isActive={false} />
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card className="h-full">
            <CardHeader className="px-4 pt-4 pb-2">
              <CardTitle className="text-base sm:text-lg font-medium">Today's Schedule</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-4 pb-4">
              <div className="space-y-1">
                <ScheduleItem time="8:45 - 10:00am" subject="English Language" />
                <ScheduleItem time="10:15 - 11:30am" subject="Mathematics" />
                <ScheduleItem time="11:30 - 12:15pm" subject="Lunch" />
                <ScheduleItem time="12:15 - 1:30pm" subject="Science" />
                <ScheduleItem time="1:45 - 3:00pm" subject="Government" />
                <ScheduleItem time="3:15 - 4:30pm" subject="Yoruba" />
                <ScheduleItem time="4:45 - 6:00pm" subject="English Language" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="sm:col-span-2 lg:col-span-1">
          <Card className="h-full">
            <CardHeader className="px-4 pt-4 pb-2">
              <CardTitle className="text-base sm:text-lg font-medium">Contacts</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-4 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                <ContactItem name="Ayodeji Omojuwa" subject="Mathematics" avatarColor="bg-blue-100" initials="AO" />
                <ContactItem name="Oluwaseun Kubo" subject="English" avatarColor="bg-gray-100" initials="OK" />
                <ContactItem name="Bamidele Oluwawibe" subject="Yoruba" avatarColor="bg-gray-100" initials="BO" />
                <ContactItem name="Timbolo Ariyo" subject="Physics" avatarColor="bg-blue-100" initials="TA" />
                <ContactItem name="Vicka Olaolu" subject="Economics" avatarColor="bg-red-100" initials="VO" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

function ExamCard({
  day,
  subject,
  dayOfWeek,
  isActive,
}) {
  return (
    <div
      className={`rounded-lg p-2 sm:p-4 flex flex-col items-center justify-center aspect-square ${
        isActive ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700"
      }`}
    >
      <div className="text-xl sm:text-2xl font-bold">{day}</div>
      {subject && <div className="text-xs sm:text-sm mt-0.5 sm:mt-1">{subject}</div>}
      <div className="text-[10px] sm:text-xs mt-auto">{dayOfWeek}</div>
    </div>
  )
}

function ScheduleItem({ time, subject }) {
  return (
    <div className="flex items-center justify-between py-2 sm:py-3 border-b border-gray-100">
      <div className="text-xs sm:text-sm text-gray-500">{time}</div>
      <div className="text-xs sm:text-sm font-medium">{subject}</div>
    </div>
  )
}

function ContactItem({
  name,
  subject,
  avatarColor,
  initials,
}) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
        <AvatarImage src="/placeholder.svg?height=40&width=40" alt={name} />
        <AvatarFallback className={avatarColor}>{initials}</AvatarFallback>
      </Avatar>
      <div>
        <h3 className="font-medium text-sm sm:text-base">{name}</h3>
        <p className="text-xs sm:text-sm text-gray-500">{subject}</p>
      </div>
    </div>
  )
}
